import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  useCountStock,
  useDeleteStockLocation,
  useMoveStock,
  useSaveStockLocation,
  useStockItems,
  useStockLocation,
  useStockLocations,
  useStockMovements,
  useUsers
} from '../api/queries'
import type { StockLevel, StockLocation, StockLocationKind } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { QrCode } from '../components/QrCode'
import { Card, EmptyState, ErrorBox, Loading } from '../components/ui'
import { api } from '../api/client'
import { config } from '../config'
import { normalizeScanned } from '../lib/scanner'
import { formatDateTime } from '../lib/format'

/** URL'en i QR-mærkatet på en kasse eller en bil. */
export function stockLocationUrl(code: string) {
  const base = config.publicUrl || window.location.origin
  return `${base.replace(/\/$/, '')}/l/${code}`
}

/** Tal med dansk komma og uden efterfølgende nuller: 12,50 bliver 12,5. */
function amount(value: number) {
  return value.toLocaleString('da-DK', { maximumFractionDigits: 3 })
}

/**
 * Lagre, biler og kasser (docs/DECISIONS.md beslutning 070).
 *
 * <b>De tre står i samme liste.</b> Lagre først, så biler, så kasserne under
 * dem, de ligger i - i den rækkefølge man tænker på dem.
 */
export function StockLocationsPage() {
  const { isProjectManager } = useAuth()
  const { data, isLoading, error } = useStockLocations({ includeInactive: true })
  const [editing, setEditing] = useState<StockLocation | 'new' | null>(null)

  if (editing) {
    return (
      <StockLocationForm
        location={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
      />
    )
  }

  const all = data ?? []
  const roots = all.filter((l) => l.kind !== 'box')
  const boxes = all.filter((l) => l.kind === 'box')

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row row--between" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h1 className="page-title" style={{ margin: 0 }}>
          Lagre og biler
        </h1>
        {isProjectManager && (
          <button className="btn btn--primary" onClick={() => setEditing('new')}>
            Nyt sted
          </button>
        )}
      </div>

      {isLoading && <Loading />}
      {error && <ErrorBox error={error} />}

      {data && all.length === 0 && (
        <EmptyState>
          Der er ingen lagre eller biler endnu. Opret firmalageret og den første bil, så kan varerne
          lægges ind.
        </EmptyState>
      )}

      {roots.map((root) => (
        <Card key={root.id}>
          <LocationRow location={root} />

          {/* Kasserne staar under det, de ligger i - saa man kan se, hvad der
              foelger med, hvis bilen koerer. */}
          {boxes
            .filter((box) => box.parentId === root.id)
            .map((box) => (
              <div key={box.id} style={{ marginLeft: 22, marginTop: 6 }}>
                <LocationRow location={box} />
              </div>
            ))}
        </Card>
      ))}

      {boxes.some((box) => !box.parentId) && (
        <Card title="Kasser uden sted">
          <p className="small muted" style={{ marginTop: 0 }}>
            De her kasser lå i noget, der er blevet fjernet. Sæt dem et sted igen.
          </p>
          {boxes
            .filter((box) => !box.parentId)
            .map((box) => (
              <LocationRow key={box.id} location={box} />
            ))}
        </Card>
      )}
    </div>
  )
}

function LocationRow({ location }: { location: StockLocation }) {
  const icon = location.kind === 'warehouse' ? '🏭' : location.kind === 'van' ? '🚐' : '📦'

  return (
    <Link
      className="row row--between device-row"
      to={`/stock/locations/${location.id}`}
      style={{ gap: 12, opacity: location.isActive ? 1 : 0.55 }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontWeight: 600 }}>
          {icon} {location.name}
          {!location.isActive && <span className="tiny muted"> · ikke i brug</span>}
        </div>
        <div className="tiny muted">
          {[
            location.reference,
            location.responsibleUser?.displayName,
            location.parentName ? `i ${location.parentName}` : null
          ]
            .filter(Boolean)
            .join(' · ') || location.kindName}
        </div>
      </div>

      <div className="tiny muted" style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
        {location.itemCount === 0 ? 'Tom' : `${location.itemCount} varer`}
        {location.boxCount > 0 && <div>{location.boxCount} kasser</div>}
      </div>
    </Link>
  )
}

/**
 * Ét sted med alt hvad der ligger i det.
 *
 * <b>En bil viser også sine kassers indhold.</b> Ellers ville den se tom ud,
 * fordi alting ligger i kasser - og så ville man lede efter en fejl, der ikke er
 * der.
 */
export function StockLocationDetailPage() {
  const { id } = useParams()
  const locationId = Number(id)

  const { isProjectManager, isAdmin } = useAuth()
  const navigate = useNavigate()

  const { data, isLoading, error } = useStockLocation(locationId)
  const { data: movements } = useStockMovements({ locationId, limit: 25 })
  const remove = useDeleteStockLocation()

  const [editing, setEditing] = useState(false)
  const [adding, setAdding] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />
  if (!data) return <ErrorBox error={new Error('Stedet findes ikke.')} />

  const { location, boxes, levels } = data

  if (editing) {
    return <StockLocationForm location={location} onClose={() => setEditing(false)} />
  }

  async function drop() {
    if (!window.confirm(`Slet "${location.name}"?`)) return

    setProblem(null)
    try {
      await remove.mutateAsync(location.id)
      navigate('/stock/locations')
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Stedet kunne ikke slettes.')
    }
  }

  return (
    <div className="stack" style={{ gap: 16 }}>
      <Card
        actions={
          isProjectManager ? (
            <div className="btn-group">
              <button className="btn btn--sm" onClick={() => setEditing(true)}>
                Rediger
              </button>
              {/* Projektleder må rette, men sletning kræver Administrator på serveren
                  (StockLocationsController.Delete) - så knappen vises kun dér. */}
              {isAdmin && (
                <button className="btn btn--sm" onClick={() => void drop()} disabled={remove.isPending}>
                  Slet
                </button>
              )}
            </div>
          ) : undefined
        }
      >
        <div className="tiny muted">
          <Link to="/stock/locations">← Lagre og biler</Link>
        </div>

        <div className="row row--between" style={{ alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h1 className="page-title" style={{ marginBottom: 4, marginTop: 8 }}>
              {location.name}
            </h1>
            <div className="muted small">
              {[
                location.kindName,
                location.reference,
                location.responsibleUser?.displayName,
                location.parentName ? `i ${location.parentName}` : null
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>

            {location.notes && (
              <div className="small" style={{ marginTop: 10, whiteSpace: 'pre-wrap' }}>
                {location.notes}
              </div>
            )}
          </div>

          {/* Kasser og biler faar et maerkat, saa de kan scannes. */}
          <div style={{ textAlign: 'center' }}>
            <QrCode value={stockLocationUrl(location.code)} size={120} />
            <div className="tiny muted" style={{ marginTop: 6, letterSpacing: 1 }}>
              {location.code}
            </div>
          </div>
        </div>
      </Card>

      {problem && <div className="error-box">{problem}</div>}

      {boxes.length > 0 && (
        <Card title={`Kasser (${boxes.length})`}>
          <p className="small muted" style={{ marginTop: 0 }}>
            Flyttes en kasse over i en anden bil, følger indholdet med - uden at der scannes en
            eneste vare.
          </p>
          <div className="stack" style={{ gap: 2 }}>
            {boxes.map((box) => (
              <LocationRow key={box.id} location={box} />
            ))}
          </div>
        </Card>
      )}

      <Card
        title={`Indhold (${levels.length})`}
        actions={
          <button className="btn btn--sm" onClick={() => setAdding((open) => !open)}>
            {adding ? 'Luk' : 'Læg noget ind'}
          </button>
        }
      >
        {adding && (
          <div style={{ marginBottom: 14 }}>
            <MoveIntoLocation location={location} onDone={() => setAdding(false)} />
          </div>
        )}

        {levels.length === 0 ? (
          <EmptyState>Der ligger ikke noget her endnu.</EmptyState>
        ) : (
          <div className="stack" style={{ gap: 2 }}>
            {levels.map((level) => (
              <LevelRow key={`${level.locationId}-${level.itemId}`} level={level} />
            ))}
          </div>
        )}
      </Card>

      {movements && movements.length > 0 && (
        <Card title="Sidste bevægelser">
          <div className="stack" style={{ gap: 2 }}>
            {movements.map((movement) => (
              <div key={movement.id} className="row row--between device-row" style={{ gap: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <div className="small">
                    {movement.kindName} · {amount(movement.quantity)} {movement.unit}{' '}
                    {movement.itemName}
                  </div>
                  <div className="tiny muted">
                    {(movement.fromLocationName ?? 'udefra') +
                      ' → ' +
                      (movement.toLocationName ?? 'ud af huset')}
                    {movement.projectName && ` · ${movement.projectName}`}
                    {movement.note && ` · ${movement.note}`}
                  </div>
                </div>
                <div className="tiny muted" style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                  {formatDateTime(movement.createdAt)}
                  <div>{movement.createdBy?.displayName}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  )
}

/**
 * Én vare på hylden, med optælling.
 *
 * <b>Et negativt tal vises rødt og forklares.</b> Det betyder ikke, at systemet
 * er i stykker - det betyder, at der er scannet mere ud end ind, og det er
 * netop den oplysning, man skal handle på.
 */
function LevelRow({ level }: { level: StockLevel }) {
  const count = useCountStock()
  const [counting, setCounting] = useState(false)
  const [value, setValue] = useState('')

  async function submit() {
    const counted = Number(value.replace(',', '.'))
    if (!Number.isFinite(counted) || counted < 0) return

    await count.mutateAsync({ locationId: level.locationId, itemId: level.itemId, counted })
    setCounting(false)
    setValue('')
  }

  const negative = level.quantity < 0

  return (
    <div className="stack" style={{ gap: 6 }}>
      <div className="row row--between device-row" style={{ gap: 10 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="small" style={{ fontWeight: 600 }}>
            {level.itemName}
          </div>
          <div className="tiny muted">
            {[level.supplierNumber, level.barcode && `Stregkode ${level.barcode}`]
              .filter(Boolean)
              .join(' · ') || level.itemCode}
            {level.locationKind === 'box' && ` · i ${level.locationName}`}
          </div>
        </div>

        <div style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
          <div
            className="small"
            style={{ fontWeight: 600, color: negative ? '#B91C1C' : level.isBelowMinimum ? '#B45309' : undefined }}
          >
            {amount(level.quantity)} {level.unit}
          </div>
          <button
            className="btn btn--sm"
            style={{ marginTop: 4 }}
            onClick={() => {
              setValue(String(level.quantity))
              setCounting((open) => !open)
            }}
          >
            Tæl op
          </button>
        </div>
      </div>

      {negative && (
        <div className="tiny" style={{ color: '#B91C1C', marginLeft: 4 }}>
          Der er scannet mere ud, end der blev scannet ind. Tæl op, så står tallet rigtigt igen.
        </div>
      )}

      {level.isBelowMinimum && !negative && (
        <div className="tiny" style={{ color: '#B45309', marginLeft: 4 }}>
          Under grænsen på {amount(level.minQuantity ?? 0)} {level.unit} - skal bestilles.
        </div>
      )}

      {counting && (
        <form
          className="row"
          style={{ gap: 8, marginLeft: 4, marginBottom: 6 }}
          onSubmit={(event) => {
            event.preventDefault()
            void submit()
          }}
        >
          <input
            autoFocus
            inputMode="decimal"
            style={{ maxWidth: 120 }}
            value={value}
            onChange={(event) => setValue(event.target.value)}
          />
          <span className="tiny muted" style={{ alignSelf: 'center' }}>
            {level.unit} ligger der faktisk
          </span>
          <button className="btn btn--sm btn--primary" type="submit" disabled={count.isPending}>
            Gem
          </button>
          <button className="btn btn--sm" type="button" onClick={() => setCounting(false)}>
            Fortryd
          </button>
        </form>
      )}
    </div>
  )
}

/** Lægger en vare ind på stedet - købt hjem, eller flyttet fra et andet sted. */
function MoveIntoLocation({ location, onDone }: { location: StockLocation; onDone: () => void }) {
  const move = useMoveStock()
  const { data: items } = useStockItems({})
  const { data: locations } = useStockLocations({})

  const [itemId, setItemId] = useState('')
  const [quantity, setQuantity] = useState('')
  const [fromId, setFromId] = useState('')
  const [problem, setProblem] = useState<string | null>(null)

  async function submit() {
    const amountValue = Number(quantity.replace(',', '.'))
    if (!itemId || !Number.isFinite(amountValue) || amountValue <= 0) {
      setProblem('Vælg en vare og skriv hvor meget.')
      return
    }

    setProblem(null)
    try {
      await move.mutateAsync({
        itemId: Number(itemId),
        fromLocationId: fromId ? Number(fromId) : null,
        toLocationId: location.id,
        quantity: amountValue,
        // Uden et "fra" er varen koebt hjem; ellers er den flyttet internt.
        kind: fromId ? 'move' : 'receipt'
      })
      setQuantity('')
      onDone()
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Det kunne ikke gemmes.')
    }
  }

  return (
    <div className="stack" style={{ gap: 8 }}>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <select style={{ minWidth: 220, flex: 1 }} value={itemId} onChange={(e) => setItemId(e.target.value)}>
          <option value="">Vælg vare</option>
          {(items ?? []).map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>

        <input
          inputMode="decimal"
          style={{ maxWidth: 110 }}
          placeholder="Antal"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
        />

        <select style={{ maxWidth: 200 }} value={fromId} onChange={(e) => setFromId(e.target.value)}>
          <option value="">Købt hjem</option>
          {(locations ?? [])
            .filter((l) => l.id !== location.id)
            .map((l) => (
              <option key={l.id} value={l.id}>
                Fra {l.name}
              </option>
            ))}
        </select>

        <button className="btn btn--primary btn--sm" onClick={() => void submit()} disabled={move.isPending}>
          {move.isPending ? 'Gemmer ...' : 'Læg ind'}
        </button>
      </div>

      {problem && <div className="error-box">{problem}</div>}
    </div>
  )
}

/** Opret eller ret et lager, en bil eller en kasse. */
function StockLocationForm({
  location,
  onClose
}: {
  location: StockLocation | null
  onClose: () => void
}) {
  const save = useSaveStockLocation()
  const { data: users } = useUsers()
  const { data: locations } = useStockLocations({})

  const [kind, setKind] = useState<StockLocationKind>(location?.kind ?? 'van')
  const [name, setName] = useState(location?.name ?? '')
  const [reference, setReference] = useState(location?.reference ?? '')
  const [parentId, setParentId] = useState(location?.parentId ? String(location.parentId) : '')
  const [responsibleId, setResponsibleId] = useState(
    location?.responsibleUser ? String(location.responsibleUser.id) : ''
  )
  const [notes, setNotes] = useState(location?.notes ?? '')
  const [isActive, setIsActive] = useState(location?.isActive ?? true)
  const [problem, setProblem] = useState<string | null>(null)

  async function submit() {
    if (!name.trim()) {
      setProblem('Stedet skal have et navn.')
      return
    }

    setProblem(null)
    try {
      await save.mutateAsync({
        id: location?.id,
        input: {
          name: name.trim(),
          kind,
          parentId: kind === 'box' && parentId ? Number(parentId) : null,
          responsibleUserId: kind === 'van' && responsibleId ? Number(responsibleId) : null,
          reference: reference.trim() || null,
          notes: notes.trim() || null,
          isActive
        }
      })
      onClose()
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Stedet kunne ikke gemmes.')
    }
  }

  // Kasser kan kun ligge i et lager eller en bil - ikke i en anden kasse.
  const holders = (locations ?? []).filter((l) => l.kind !== 'box' && l.id !== location?.id)

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row row--between" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h1 className="page-title" style={{ margin: 0 }}>
          {location ? `Rediger ${location.name}` : 'Nyt sted'}
        </h1>
        <div className="btn-group">
          <button className="btn" onClick={onClose}>
            Fortryd
          </button>
          <button className="btn btn--primary" onClick={() => void submit()} disabled={save.isPending}>
            {save.isPending ? 'Gemmer ...' : 'Gem'}
          </button>
        </div>
      </div>

      {problem && <div className="error-box">{problem}</div>}

      <Card>
        <div className="stack" style={{ gap: 12 }}>
          <label className="stack" style={{ gap: 4 }}>
            <span className="tiny muted">Hvad er det?</span>
            {location ? (
              <div className="small" style={{ paddingTop: 4 }}>
                {location.kindName}
                <div className="tiny muted">
                  Kan ikke ændres - en bil bliver ikke til en kasse. Opret et nyt sted, og flyt
                  indholdet.
                </div>
              </div>
            ) : (
              <select value={kind} onChange={(e) => setKind(e.target.value as StockLocationKind)}>
                <option value="warehouse">Lager</option>
                <option value="van">Bil</option>
                <option value="box">Kasse</option>
              </select>
            )}
          </label>

          <label className="stack" style={{ gap: 4 }}>
            <span className="tiny muted">Navn</span>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={kind === 'van' ? 'Fx Bil 3' : kind === 'box' ? 'Fx Kasse A' : 'Fx Firmalageret'}
            />
          </label>

          <label className="stack" style={{ gap: 4 }}>
            <span className="tiny muted">
              {kind === 'van' ? 'Nummerplade' : kind === 'box' ? 'Hvor i bilen' : 'Hyldenummer'}
            </span>
            <input value={reference} onChange={(e) => setReference(e.target.value)} />
          </label>

          {kind === 'box' && (
            <label className="stack" style={{ gap: 4 }}>
              <span className="tiny muted">Ligger i</span>
              <select value={parentId} onChange={(e) => setParentId(e.target.value)}>
                <option value="">Vælg bil eller lager</option>
                {holders.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
              <span className="tiny muted">
                Flyttes kassen senere, følger indholdet med - uden at der scannes en eneste vare.
              </span>
            </label>
          )}

          {kind === 'van' && (
            <label className="stack" style={{ gap: 4 }}>
              <span className="tiny muted">Hvem har bilen</span>
              <select value={responsibleId} onChange={(e) => setResponsibleId(e.target.value)}>
                <option value="">Ingen</option>
                {(users ?? []).map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.displayName}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className="stack" style={{ gap: 4 }}>
            <span className="tiny muted">Noter</span>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </label>

          <label className="row" style={{ gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            <span className="small">
              I brug
              <span className="tiny muted"> - en solgt bil har stadig en historik, så sæt den ud af brug i stedet for at slette</span>
            </span>
          </label>
        </div>
      </Card>
    </div>
  )
}

/**
 * Landingssiden for QR-koden paa en kasse eller en bil (`/l/:code`).
 *
 * Maerkatet indeholder en URL, saa mobilens eget kamera ogsaa virker. Koden
 * slaas op, og saa sendes man videre til stedet selv.
 */
export function StockLocationLandingPage() {
  const { code } = useParams()
  const navigate = useNavigate()
  const [message, setMessage] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    void api
      .get<{ location: StockLocation }>(`/api/stock/locations/by-code/${normalizeScanned(code ?? '')}`)
      .then((found) => {
        if (!cancelled) navigate(`/stock/locations/${found.location.id}`, { replace: true })
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setMessage(err instanceof Error ? err.message : 'Stedet kunne ikke slaas op.')
        }
      })

    return () => {
      cancelled = true
    }
  }, [code, navigate])

  if (message) {
    return (
      <Card title="Blev ikke fundet">
        <div className="small muted">{message}</div>
        <p className="small muted">
          Koden paa maerkatet var <code>{code}</code>.
        </p>
        <Link className="btn btn--sm" to="/stock/locations">
          Til lagre og biler
        </Link>
      </Card>
    )
  }

  return <Loading />
}
